import moment from "moment";
import { PickupReviewStatus } from "../../../../app/data/communications/models";
import {
  PickupRequestDetail,
  PickupRequestReviewDecision,
  PickupRequestReviewTask,
} from "../../../../app/data/pickup-requests/models";

export interface PickupRequestReviewAuditView {
  reviewStatus: PickupReviewStatus;
  isApproved: boolean;
  isHumanReview: boolean;
  bannerTitle: string;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
  notesLabel: string;
}

export interface PickupRequestReviewAuditHistoryItem {
  title: string;
  at?: string;
  by?: string;
}

const DECIDED_STATUSES: PickupReviewStatus[] = [
  "APPROVED_BY_HUMAN",
  "REJECTED_BY_HUMAN",
  "APPROVED_BY_SYSTEM",
  "REJECTED_BY_SYSTEM",
];

export const isPickupReviewDecided = (reviewStatus?: string): reviewStatus is PickupReviewStatus =>
  !!reviewStatus && DECIDED_STATUSES.includes(reviewStatus as PickupReviewStatus);

export const formatPickupReviewAuditDateTime = (value?: string | null) =>
  value ? moment(value).format("MM/DD/YY · HH:mm") : "—";

const readString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const isRejectedDecision = (decision?: string) => decision?.toUpperCase() === "REJECTED";

const sortNewestFirst = <T extends { createdAt?: string }>(items: T[]) =>
  [...items].sort(
    (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
  );

const getBannerTitle = (
  reviewStatus: PickupReviewStatus,
  isApproved: boolean
): string => {
  switch (reviewStatus) {
    case "APPROVED_BY_HUMAN":
      return "Pickup created";
    case "REJECTED_BY_HUMAN":
      return "Pickup request rejected";
    case "APPROVED_BY_SYSTEM":
      return "Auto-approved";
    case "REJECTED_BY_SYSTEM":
      return "Auto-rejected";
    default:
      return isApproved ? "Approved" : "Rejected";
  }
};

const getDecisionHistoryTitle = (
  reviewStatus: PickupReviewStatus,
  decision: PickupRequestReviewDecision
): string => {
  if (isRejectedDecision(decision.decision)) {
    return reviewStatus === "REJECTED_BY_SYSTEM"
      ? "Auto-rejected"
      : "Pickup request rejected";
  }
  return reviewStatus === "APPROVED_BY_SYSTEM" ? "Auto-approved" : "Pickup created";
};

interface DecisionWithTask {
  task: PickupRequestReviewTask;
  decision: PickupRequestReviewDecision;
}

const collectDecisionsWithTasks = (detail: PickupRequestDetail): DecisionWithTask[] =>
  (detail.reviewTasks ?? []).flatMap((task) =>
    (task.decisions ?? []).map((decision) => ({ task, decision }))
  );

const getLatestDecisionWithTask = (
  detail: PickupRequestDetail
): DecisionWithTask | undefined => {
  const withTasks = collectDecisionsWithTasks(detail);
  if (withTasks.length === 0) return undefined;

  return sortNewestFirst(
    withTasks.map(({ task, decision }) => ({
      task,
      decision,
      createdAt: decision.createdAt,
    }))
  )[0];
};

const resolveFromLegacyFields = (detail: PickupRequestDetail, isApproved: boolean) => {
  const extended = detail as PickupRequestDetail & Record<string, unknown>;

  const reviewedAt =
    readString(detail.reviewedAt) ??
    readString(extended.humanReviewedAt) ??
    readString(extended.approvedAt) ??
    readString(extended.rejectedAt);

  const reviewedBy =
    readString(detail.reviewedBy) ??
    readString(extended.humanReviewedBy) ??
    readString(extended.approvedBy) ??
    readString(extended.rejectedBy);

  const notes = isApproved
    ? readString(detail.reviewNotes) ??
      readString(extended.notes) ??
      readString(extended.approveNotes)
    : readString(detail.rejectionReason) ??
      readString(extended.rejectReason) ??
      readString(extended.reason);

  return { reviewedAt, reviewedBy, notes };
};

/** Resolve audit banner data from review task decisions (preferred) or legacy top-level fields. */
export const resolvePickupRequestReviewAudit = (
  detail: PickupRequestDetail
): PickupRequestReviewAuditView | null => {
  if (!isPickupReviewDecided(detail.reviewStatus)) return null;

  const reviewStatus = detail.reviewStatus as PickupReviewStatus;
  const latest = getLatestDecisionWithTask(detail);

  const isApproved = latest
    ? !isRejectedDecision(latest.decision.decision)
    : reviewStatus === "APPROVED_BY_HUMAN" || reviewStatus === "APPROVED_BY_SYSTEM";

  const isHumanReview =
    reviewStatus === "APPROVED_BY_HUMAN" || reviewStatus === "REJECTED_BY_HUMAN";

  const legacy = resolveFromLegacyFields(detail, isApproved);

  const reviewedAt =
    readString(latest?.decision.createdAt) ??
    readString(latest?.task.completedAt) ??
    legacy.reviewedAt;

  const reviewedBy =
    readString(latest?.decision.reviewerId) ??
    readString(latest?.task.assignee ?? undefined) ??
    legacy.reviewedBy ??
    (reviewStatus.endsWith("_BY_SYSTEM") ? readString(latest?.task.createdBy) : undefined);

  const notes = readString(latest?.decision.notes) ?? legacy.notes;

  return {
    reviewStatus,
    isApproved,
    isHumanReview,
    bannerTitle: getBannerTitle(reviewStatus, isApproved),
    reviewedAt,
    reviewedBy,
    notes,
    notesLabel: isApproved ? "Review Notes" : "Rejection reason",
  };
};

export const getPickupRequestReviewAuditHistory = (
  detail: PickupRequestDetail,
  audit: PickupRequestReviewAuditView
): PickupRequestReviewAuditHistoryItem[] => {
  const decisions = sortNewestFirst(
    collectDecisionsWithTasks(detail).map(({ task, decision }) => ({
      task,
      decision,
      createdAt: decision.createdAt,
    }))
  );

  const decisionItems: PickupRequestReviewAuditHistoryItem[] = decisions.map(({ task, decision }) => ({
    title: getDecisionHistoryTitle(audit.reviewStatus, decision),
    at: decision.createdAt ?? task.completedAt,
    by: readString(decision.reviewerId) ?? readString(task.createdBy),
  }));

  if (decisionItems.length > 0) {
    return [
      ...decisionItems,
      {
        title: "Pickup request received",
        at: detail.requestReceivedAt,
      },
    ];
  }

  if (audit.reviewedAt || audit.reviewedBy) {
    return [
      {
        title: audit.bannerTitle,
        at: audit.reviewedAt,
        by: audit.reviewedBy,
      },
      {
        title: "Pickup request received",
        at: detail.requestReceivedAt,
      },
    ];
  }

  return [
    {
      title: "Pickup request received",
      at: detail.requestReceivedAt,
    },
  ];
};
